import { FC, memo, useCallback } from "react";
import styles from "./ProjectCard.module.css";

import { message, Space, Tooltip } from "antd";
import { deleteSubmissionRequest, IProject } from "api/project";
import { faEye, faHeart, faTrash } from "@fortawesome/free-solid-svg-icons";
import { faCommentAlt, faEnvelope } from "@fortawesome/free-regular-svg-icons";

import { IconText } from "components/IconText";
import { Surface } from "components/Surface";
import { Image } from "components/Image";
import { Avatar, AvatarGroup } from "components/Avatar";
import { getImageUrl } from "utils/urlParser";
import { hackathonDetailParams, routesEnum } from "pages/Routes";
import { useMutation } from "react-query";
import { AxiosError } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";
import { Hidden } from "components/Hidden";
import { Tag } from "components/Tag";
import { Button } from "components/Button";
import { Link } from "components/Link";
import { useHackathon } from "api/hackathon";
import { useProjectEditStep } from "hooks/useProjectEditStep";
import { isAfterToday, isBeforeToday } from "hooks/useDeadline";
import { initDate } from "utils/dateUtils";
import { useTimezone } from "hooks/useTimezone";
import { GradientButton } from "components/GradientButton";
import { EyeOutline, MessageOutline } from "assets/svgs";

interface ProjectCardProps {
  project: IProject;
  hackathonId: string;
  submissionId?: string;
  status_inProgress?: "draft" | "submit" | "pending";
  onDelete?: () => void;
  withFooter?: boolean;
}

/**
 * *Render in Hackathon my-project tab
 */
const ProjectCard: FC<ProjectCardProps> = ({
  project,
  hackathonId,
  submissionId,
  status_inProgress,
  onDelete,
  withFooter = true,
}) => {
  const { data: hackathon } = useHackathon(hackathonId, { enabled: false });
  const { mutate: deleteProject } = useMutation(deleteSubmissionRequest, {
    onSuccess: () => {
      onDelete && onDelete();
    },
    onError: (err: AxiosError) => {
      message.error(err.response?.data?.message);
    },
  });
  const { totalCount, passCount } = useProjectEditStep(
    project,
    hackathon,
    status_inProgress
  );

  const timezone = useTimezone(hackathon);

  const onSubmissionTime = useMemo(() => {
    if (hackathon?.submit_late) return true;
    const { value } = timezone;
    const startDate = initDate(hackathon?.submission?.startDate, value);
    const deadline = initDate(hackathon?.submission?.deadline, value);
    return isAfterToday(startDate, value) && isBeforeToday(deadline, value);
  }, [hackathon]);

  const canDelete = useMemo(() => {
    return (
      Boolean(project?.tagLine) === false &&
      Boolean(submissionId) &&
      onSubmissionTime
    );
  }, [project, submissionId, onSubmissionTime]);

  const handleDeleteProject = (e: any) => {
    e.preventDefault();
    submissionId && deleteProject(submissionId);
  };

  // x can be array [id] || number
  const getCount = useCallback(
    (x: any) => {
      if (typeof x === "number") return x;
      if (typeof x === "object") return x?.length || 0;
      return 0;
    },
    [project]
  );

  const categories = useMemo(() => {
    let results: string[] = [];
    if (project.categories && project.categories.length !== 0) {
      if (Array.isArray(project?.categories)) {
        results = [...results, ...(project?.categories as any)];
      } else {
        results = [...results, project?.categories as any];
      }
    }

    if (project?.childTag) {
      results = [...results, project.childTag];
    }

    return results;
  }, [project]);

  return (
    <div className={styles.root}>
      {project?.winner?.includes(hackathon?._id as string) && (
        <>
          <div className={styles.ribbon} />
          <div className={styles.ribbonText}>Winner</div>
        </>
      )}
      <Surface className={styles.card}>
        <div className={styles.imageWrapper}>
          <Image className="h-100" src={getImageUrl(project?.thumbnailImage)} />
          {canDelete && (
            <Button
              onClick={handleDeleteProject}
              danger
              type="primary"
              size="small"
              className={styles.btnDelete}
              icon={<FontAwesomeIcon icon={faTrash} className="mr-8" />}
            >
              Delete
            </Button>
          )}

          <Hidden js cond={!!status_inProgress && onSubmissionTime}>
            <GradientButton size="small" className={styles.btnEdit}>
              <Link
                to={`/${hackathon?.essentials.url}${routesEnum.hackathon}/${hackathonDetailParams.myProject}/${hackathonId}/edit-project?projectId=${project?._id}`}
              >
                EDIT PROJECT
              </Link>
            </GradientButton>
          </Hidden>

          <Hidden js cond={!onSubmissionTime}>
            <GradientButton size="small" className={styles.btnEdit}>
              <Link to={`${routesEnum.project}/${project?._id}`}>
                VIEW PROJECT
              </Link>
            </GradientButton>
          </Hidden>
        </div>

        <div className={styles.cardPadding}>
          <div className="mb-8">
            <Space>
              <Tag
                type="secondary"
                rounded={false}
                className="semi-bold uppercase"
              >
                {status_inProgress === "submit"
                  ? "submitted"
                  : status_inProgress}
              </Tag>
              <b>Step {`${passCount}/${totalCount}`}</b>
            </Space>
          </div>

          <Space direction="vertical" size={6}>
            <b className="poppins text-22 bold secondary break">
              {project?.name || "Untitled"}
            </b>
            <div className="lineClamp break">{project?.tagLine}&emsp;</div>
          </Space>
        </div>
      </Surface>

      <Hidden js cond={withFooter}>
        <div className={styles.actionWrapper}>
          <Link className="white" to={`${routesEnum.project}/${project._id}`}>
            <GradientButton
              className="mr-8"
              color="secondary"
              size="small"
              icon={<FontAwesomeIcon icon={faEye} className="mr-8" />}
            >
              View
            </GradientButton>
          </Link>

          <Link
            className="white w-100"
            to={`/${hackathon?.essentials.url}${routesEnum.hackathon}/${hackathonDetailParams.myProject}/${hackathonId}/messages?submissionId=${submissionId}`}
          >
            <GradientButton
              block
              color="secondary"
              size="small"
              icon={<FontAwesomeIcon icon={faEnvelope} className="mr-8" />}
            >
              MESSAGE MANAGER
            </GradientButton>
          </Link>
        </div>
      </Hidden>
      <div className={styles.actionWrapper}>
        <div className={styles.statistic}>
          <Space className={styles.icon} size={10} align="start">
            <EyeOutline />
            {getCount(project?.countViews)}
          </Space>

          <Space size={10} align="start">
            <FontAwesomeIcon color="#FF5722" icon={faHeart} />
            {getCount(project?.likes)}
          </Space>

          <Space className={styles.icon} size={10} align="start">
            <MessageOutline />
            {getCount(project?.topics)}
          </Space>

          <AvatarGroup
            maxCount={2}
            size={32}
            maxStyle={{ color: "#f56a00", backgroundColor: "#fde3cf" }}
          >
            <Tooltip title={project?.projectOwner?.userName}>
              <Avatar src={getImageUrl(project?.projectOwner?.avatar_link)} />
            </Tooltip>
          </AvatarGroup>
        </div>
      </div>
      <div className={styles.actionWrapper}>
        {categories?.length !== 0 && (
          <Space className={styles.tagWrapper}>
            {categories?.map((x) => (
              <Tooltip key={x} title={x}>
                <Tag className={styles.tag}>
                  <svg width="6" height="7" viewBox="0 0 6 7" fill="none">
                    <circle
                      cx="3"
                      cy="3.5"
                      r="3"
                      fill="#080A25"
                      fillOpacity="0.7"
                    />
                  </svg>
                  {x}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        )}
      </div>
    </div>
  );
};

export default memo(ProjectCard);
